import {Player} from '@lottiefiles/react-lottie-player';
import React, {useEffect, useMemo, useState} from 'react';
import {Box, styled} from '@mui/material';
import {
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';

const LOTTIE_LOGO =
  '/assets/content-lab/lotties/ltx-scaler-logo-animated-2.json';

const LOGO_SIZE = 38;
const FADE_IN_DURATION = 300;
const FADE_OUT_DURATION = 800;

const Container = styled(Box)(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  zIndex: 12,
  pointerEvents: 'none',
  height: '75.8px',
  width: '215px',
}));

const PlayerContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: LOGO_SIZE,
  height: LOGO_SIZE,
  overflow: 'hidden',
}));

const TextContainer = styled(Box)<{isFadingIn: boolean}>(({isFadingIn}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  transition: isFadingIn
    ? `opacity ${FADE_IN_DURATION}ms ease-in-out`
    : `opacity ${FADE_OUT_DURATION}ms ease-in-out`,
}));

interface LtxScalerProjectLoaderProps {
  isVisible?: boolean;
  textMessages: string[];
  progressPercentage: number | null;
  hasExceededEstimate: boolean;
}

function LtxScalerProjectLoader({
  isVisible = true,
  textMessages,
  progressPercentage,
  hasExceededEstimate,
}: LtxScalerProjectLoaderProps) {
  const currentMessageIndex = useMemo(() => {
    if (textMessages.length === 0) {
      return null;
    }

    // If estimate has been exceeded, show last message
    if (hasExceededEstimate) {
      return textMessages.length - 1;
    }

    // If not started yet (no progress data), don't show anything
    if (progressPercentage === null) {
      return null;
    }

    const index = Math.floor((progressPercentage / 100) * textMessages.length);
    return Math.min(index, textMessages.length - 1);
  }, [progressPercentage, textMessages.length, hasExceededEstimate]);

  const [currentTextIndex, setCurrentTextIndex] = useState<number | null>(
    currentMessageIndex
  );
  const [textOpacity, setTextOpacity] = useState(1);
  const [isFadingIn, setIsFadingIn] = useState(true);

  useEffect(() => {
    if (
      !isVisible ||
      textMessages.length === 0 ||
      currentMessageIndex === null
    ) {
      return undefined;
    }

    if (currentTextIndex === null) {
      setCurrentTextIndex(currentMessageIndex);
      setTextOpacity(1);
      setIsFadingIn(true);
      return undefined;
    }

    if (currentMessageIndex !== currentTextIndex) {
      setTextOpacity(0);
      setIsFadingIn(false);

      const fadeOutTimeout = setTimeout(() => {
        setCurrentTextIndex(currentMessageIndex);
        setIsFadingIn(true);
        setTextOpacity(1);
      }, FADE_OUT_DURATION);

      return () => clearTimeout(fadeOutTimeout);
    }

    return undefined;
  }, [isVisible, currentMessageIndex, currentTextIndex, textMessages.length]);

  if (!isVisible || currentTextIndex === null) {
    return null;
  }

  return (
    <Container>
      <PlayerContainer>
        <Player
          src={LOTTIE_LOGO}
          autoplay
          loop
          style={{
            width: LOGO_SIZE,
            height: LOGO_SIZE,
            transform: 'scale(3)',
            transformOrigin: 'center center',
          }}
        />
      </PlayerContainer>
      <TextContainer isFadingIn={isFadingIn} style={{opacity: textOpacity}}>
        <Icon
          size="small"
          appearance="neutral"
          name="Actions-Patch-Accept-Fill"
          color={designSystemToken('semantic.bg.primary')}
        />
        <Headline size="xs" color={designSystemToken('semantic.fg.inverse')}>
          {translate(textMessages[currentTextIndex])}
        </Headline>
      </TextContainer>
    </Container>
  );
}

export default LtxScalerProjectLoader;
